import React from 'react'
import { useArcTranslation, useMantineTheme } from 'hooks'
import { Modal, Text, Group, Stack, Button } from 'components'
import { IconCircleCheck } from '@tabler/icons-react'

const SUCCESS_ICON_SIZE = 40

interface AddToMyProjectsModalProps {
  opened: boolean
  loading: boolean
  success: boolean
  onClose: () => void
  onConfirm: () => void
}

// AR-857: confirmation modal for "Add to my projects". Stays open with the
// confirm button loading until the template copy request resolves, so the
// user only lands on the new project once it is created and ready.
// Follows Apple HIG alert conventions: compact, no close button (Cancel is
// the way out), explicit choice required, primary action on the right. On
// success it briefly shows a completion state before the modal fades out
// and the app navigates, so the context switch never feels abrupt.
export const AddToMyProjectsModal: React.FC<AddToMyProjectsModalProps> = ({
  opened,
  loading,
  success,
  onClose,
  onConfirm,
}) => {
  const { t } = useArcTranslation()
  const theme = useMantineTheme()

  return (
    <Modal
      centered
      size='xs'
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={!loading && !success}
    >
      {success ? (
        <Stack align='center' spacing='xs' py='sm'>
          <IconCircleCheck size={SUCCESS_ICON_SIZE} color={theme.colors.teal[6]} />
          <Text size='sm' weight={600} align='center'>
            {t('arc_projects.add_project_success_title')}
          </Text>
        </Stack>
      ) : (
        <>
          <Text size='md' weight={600}>
            {t('arc_projects.add_project_modal_title')}
          </Text>
          <Text size='sm' color='dimmed' mt={4}>
            {t('arc_projects.add_project_modal_description')}
          </Text>
          <Group spacing='sm' position='right' mt='md'>
            <Button variant='default' onClick={onClose} disabled={loading}>
              {t('general_buttons_cancel')}
            </Button>
            <Button variant='filled' color='teal' onClick={onConfirm} loading={loading}>
              {t('arc_projects.add_project_confirm')}
            </Button>
          </Group>
        </>
      )}
    </Modal>
  )
}
